<template>
  <div>
    <page-header
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <filter-manager
      v-model="searchModel"
      class="mb-4"
      :filters="filtersConfig"
      @provider="onChangeProviderHandler"
      @search="onSearch(true)"
    />
    <custom-separator
      class="mt-1 mb-4"
      text="Resultado de Servicios Compensatorios"
    />
    <custom-result-not-found
      v-if="results.length == 0"
      :text="'No hay información disponible.'"
    />
    <div v-else>
      <div v-for="(result, index) in results" :key="index">
        <result-row
          :value="result"
          @on-open-additional-information="
            () => setActiveViewDisplay(viewDisplayEnum.DOCUMENTS, index)
          "
          @on-open-close="onOpenCloseHandler(index, result)"
          @on-open-documents="
            () => setActiveViewDisplay(viewDisplayEnum.DOCUMENTS, index)
          "
          @on-open-request-histories="onOpenRequestStatusLog(index, result)"
          @on-open-return-to-previous-status="
            onOpenReturnToPreviousStatusHandler(index, result, $event)
          "
          @on-show-approved="onHandleApproved(result, index)"
          @on-show-deny="setActiveViewDisplay(viewDisplayEnum.DENY, index)"
          @on-show-pending-approval="
            setActiveViewDisplay(viewDisplayEnum.PENDING_APROVAL, index)
          "
          @on-show-reject="setActiveViewDisplay(viewDisplayEnum.REJECT, index)"
          @on-show-submit="setActiveViewDisplay(viewDisplayEnum.SUBMIT, index)"
          @refresh="onSearch"
        />
      </div>
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        show-text
        @pagination-go-page="onGoToPage"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import ResultRow from './components/ResultRow.vue';
import Pagination from 'pagination';

import {
  getInitFilters as _getInitFilters,
  search as _search,
  getStatusLogByRequestId as _getStatusLogByRequestId,
} from '@/services/provider/compensatoryRequest.api';

import { getProviders as _getProviders } from '@/services/uspqrp/contract.api.js';

import { getSpecialistsByProvider as _getSpecialistsByProvider } from '@/services/provider/specialist.api';

import viewDisplay from '@/utils/constants/compensatoryRequestViewDisplay';
import compensatoryRequestStatus from '@/utils/constants/compensatoryRequestStatus';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';
import roles from '@/utils/constants/roles';

export default {
  name: 'CompensatoryServiceManagement',
  mixins: [alertMixin],
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    ResultRow,
    Pagination,
  },
  data() {
    return {
      searchModel: {
        academicYear: null,
        status: null,
        searchText: null,
        requestType: null,
        disciplineType: null,
        provider: null,
        specialist: null,
        origin: null,
      },
      filterOptions: {
        academicYears: [],
        statuses: [],
        requestTypes: [],
        disciplines: [],
        providers: [],
        specialists: [],
        origins: [
          { id: 'SAEE', name: 'SAEE' },
          { id: 'RP', name: 'RP' },
        ],
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 1,
        pageCount: 1,
        count: 1,
        pageSize: 10,
      },
      results: [],
    };
  },
  computed: {
    viewDisplayEnum() {
      return viewDisplay;
    },
    headerConfig() {
      return {
        title: 'Manejo de Servicios Compensatorios',
        icon: 'fas fa-user-tie',
        routes: ['Proveedor', 'Manejo de Servicios Compensatorios'],
      };
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
        },
        {
          name: 'searchText',
          component: 'CustomInput',
          placeholder:
            'Escriba Número de Solicitud, Nombre del Estudiante o SIE',
          value: this.searchModel.input,
          class: 'col-12 col-md-6',
        },
        {
          name: 'requestType',
          component: 'CustomDropDown',
          options: this.filterOptions.requestTypes,
          label: 'Tipo de Solicitud:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
          disabled: this.filterOptions.requestTypes.length === 1,
        },
        {
          name: 'disciplineType',
          component: 'CustomDropDown',
          options: this.filterOptions.disciplines,
          label: 'Disciplina:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filterOptions.providers,
          label: 'Proveedor:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
          clearable: true,
          disabled: this.hasOnlyOneCompanyToFilter,
        },
        {
          name: 'specialist',
          component: 'CustomDropDown',
          options: this.filterOptions.specialists,
          label: 'Especialista:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
          clearable: true,
          disabled: this.hasOneSpecialistToFilter,
        },
        {
          name: 'origin',
          component: 'CustomDropDown',
          options: this.filterOptions.origins,
          label: 'Procedencia:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
          clearable: true,
        },
      ];
    },
    hasOnlyOneCompanyToFilter() {
      return this.filterOptions.providers?.length === 1;
    },
    hasOneSpecialistToFilter() {
      return this.filterOptions?.specialists?.length === 1;
    },
    userCurrentCompany() {
      return this.userCompanies?.length === 1 ? this.userCompanies[0] : null;
    },
  },
  methods: {
    async getInitFilters() {
      const { data: response } = await _getInitFilters();
      if (Object.values(response).length) {
        this.filterOptions.academicYears = response.academicYears;
        this.filterOptions.statuses = response.statuses;
        this.filterOptions.requestTypes = response.requestTypes;
        this.filterOptions.disciplines = response.disciplines;
        this.filterOptions = {
          ...this.filterOptions,
          providers: this.userCompanies?.length
            ? this.userCompanies
            : response.providers,
        };
        if (this.filterOptions.academicYears.length > 0) {
          this.searchModel.academicYear = this.filterOptions.academicYears[0];
        }

        if (this.filterOptions.requestTypes.length === 1) {
          this.searchModel.requestType = this.filterOptions.requestTypes[0];
        }

        if (this.hasOnlyOneCompanyToFilter) {
          this.searchModel.provider = this.filterOptions.providers[0];
        }
        if (this.hasOnlyOneCompanyToFilter) {
          await this.getSpecialists(
            this.searchModel.academicYear.id,
            this.searchModel.provider.id
          );
        }
        if (
          this.userRoleId != roles.Assistant_Provider_RP &&
          this.userRoleId != roles.Provider_RP &&
          this.userRoleId != roles.Specialist_RP
        ) {
          await this.onSearch();
        } else {
          if (this.searchModel?.provider?.id) {
            await this.onSearch();
          }
        }
      }
    },
    async getProviders(academicYearId) {
      if (academicYearId) {
        this.filterOptions.providers = (
          await _getProviders(academicYearId)
        ).data;

        if (this.hasOnlyOneCompanyToFilter) {
          this.searchModel.provider = this.filterOptions.providers[0];
        }
      }
    },
    async getSpecialists(academicYearId, providerId) {
      if (academicYearId && providerId) {
        this.filterOptions.specialists = (
          await _getSpecialistsByProvider(academicYearId, providerId, true)
        ).data;

        if (this.hasOneSpecialistToFilter) {
          this.searchModel.specialist = this.filterOptions.specialists[0];
        }
      }
    },
    async onChangeProviderHandler(value) {
      this.searchModel.specialist = null;
      this.filterOptions.specialists = [];
      if (value?.id) {
        await this.getSpecialists(this.searchModel.academicYear?.id, value.id);
      }
    },
    async onSearch(IsSearch) {
      if (
        this.userRoleId == roles.Assistant_Provider_RP ||
        this.userRoleId == roles.Provider_RP ||
        this.userRoleId == roles.Specialist_RP
      ) {
        if (!this.searchModel?.provider?.id && IsSearch) {
          this.ShowToast(
            'Alerta',
            `Favor seleccione un proveedor de la lista de proveedores`,
            'error'
          );
          return;
        }
      }

      let payload = {
        academicYearId: this.searchModel.academicYear?.id,
        statusId: this.searchModel.status?.id,
        searchText: this.searchModel.searchText,
        requestTypeId: this.searchModel.requestType?.id,
        disciplineTypeId: this.searchModel.disciplineType?.id,
        providerId: this.searchModel.provider?.id,
        origin: this.searchModel.origin?.id,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
        isSearch: IsSearch,
      };

      await _search(payload).then(({ data }) => {
        this.results = data.data.map((d) => ({
          ...d,
          isOpen: false,
          viewToDisplay: this.viewDisplayEnum.UNSET,
          statusLogs: [],
          documents: [],
        }));

        this.pagination.count = data.count;
        this.pagination.currentPage = data.currentPage;
        this.pagination.pageCount = data.pageCount;
        this.pagination.totalItemCount = data.totalItemCount;
      });
    },
    onOpenCloseHandler(index, row) {
      if (row.isOpen) {
        this.setActiveViewDisplay(this.viewDisplayEnum.UNSET, index, {});
        this.results = this.results.map((x) => ({
          ...x,
          isOpen: false,
        }));
      } else {
        this.setActiveViewDisplay(this.viewDisplayEnum.DOCUMENTS, index, {});
        this.results = this.results.map((x, rowIndex) => ({
          ...x,
          isOpen: rowIndex === index,
        }));
      }
    },
    async onOpenRequestStatusLog(index, item) {
      await _getStatusLogByRequestId(item.requestId).then(({ data }) => {
        item.statusLogs = data;
        this.setActiveViewDisplay(this.viewDisplayEnum.HISTORY_REQUEST, index);
      });
    },
    onHandleApproved(item, index) {
      if (
        item.statusId ===
        compensatoryRequestStatus.CompletadoPendientedeAprobacion
      ) {
        this.setActiveViewDisplay(
          this.viewDisplayEnum.APPROVE_COMPLETE_PENDING_APPROVAL,
          index
        );
      } else {
        this.setActiveViewDisplay(this.viewDisplayEnum.ACCEPT, index);
      }
    },
    setActiveViewDisplay(viewDisplay, index, prop) {
      this.results = this.results.map((x, idx) => ({
        ...x,
        isOpen: idx === index,
        viewToDisplay: idx === index ? viewDisplay : this.viewDisplayEnum.UNSET,
        ...(idx === index ? prop : {}),
      }));
    },
    async onGoToPage(page) {
      this.pagination.currentPage = page;
      this.onSearch();
    },
    onOpenReturnToPreviousStatusHandler(index, row, event) {
      this.setActiveViewDisplay(
        this.viewDisplayEnum.RETURN_TO_PREVIOUS_STATUS,
        index,
        { returnToNewStatus: event }
      );
    },
  },
  async mounted() {
    await this.getInitFilters();
  },
};
</script>
